import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Switch } from '@mui/material'

import { TARIFFS_TABLE_ACTIONS, TARIFFS_TABLE_COLUMNS } from '../../constants'
import { TableUrlPagination } from '../../../../ui'
import { useRowSelector } from '../../../loader/hooks'
import { tariffsDataPreparation } from '../../logics'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const TariffsTable = () => {
  const intl = useIntl()
  const role = useSelector((state) => state.global.role)
  const tariffLoader = useSelector((state) => state.loader.tariffs)
  const data = tariffLoader?.data?.tarifas || []
  const totalEntries = tariffLoader?.data?.total_tarifas
  const search = tariffLoader?.config?.params
  const loading = tariffLoader?.loading

  const combos = useSelector((state) => state.combos)
  const navigate = useNavigate()

  const { addSelection } = useRowSelector({
    identifier: 'tariffs',
    selectFilter: (state) => state?.data?.tarifas,
    idField: 'tarifa_id',
  })

  const handleGoToDetail = (e, row) => {
    navigate(`/tariffs/${row.tarifa_id}`)
  }

  const handleSelectRows = useCallback(
    (rows) => {
      addSelection(rows)
    },
    [addSelection]
  )

  const formattedColumns = useMemo(
    () =>
      [...TARIFFS_TABLE_COLUMNS, ...TARIFFS_TABLE_ACTIONS].map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: intl.formatMessage({ id: `pages.tariffs.table.columns.${column.field}.title` }),
          size: column.minWidth,
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            header: intl.formatMessage({ id: `global.${column.field}` }),
            Cell: (props) => (
              <div onClick={(e) => e.stopPropagation()}>
                <span>Acciones</span>
              </div>
            ),
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (column.field === 'active') {
          return {
            ...baseColumn,
            header: intl.formatMessage({ id: `global.${column.field}` }),
            Cell: ({ cell }) => (
              <div onClick={(e) => e.stopPropagation()}>
                <Switch checked={cell.getValue()} disabled />
              </div>
            ),
            muiTableBodyCellProps: { align: 'center' },
          }
        }

        if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return <span>{lookup[value] ?? value ?? '--'}</span>
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
        }
      }),
    [combos, intl]
  )

  const processedData = useMemo(() => tariffsDataPreparation(data, { intl }), [data, intl])

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: role === 'e_commerce',
      draggable: false,
    }),
    [role]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onSelectionChange={handleSelectRows}
      onRowClick={handleGoToDetail}
    />
  )
}
